import React, { useState, useEffect } from 'react';
import './SROChatQuotaStatus.css';

/**
 * SRO Chat Quota Status Component
 * Displays user's current token quota, usage, and reset date
 */
export const SROChatQuotaStatus = ({ userId, showDetails = false }) => {
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchQuota();
    // Refresh every 5 minutes
    const interval = setInterval(fetchQuota, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [userId]);

  const fetchQuota = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/sro/quota', {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch quota');

      const data = await response.json();
      setQuota(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching quota:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="quota-status loading">Loading quota information...</div>;
  }

  if (error) {
    return <div className="quota-status error">Error: {error}</div>;
  }

  if (!quota) {
    return <div className="quota-status error">No quota information available</div>;
  }

  const percentageUsed = parseFloat(quota.percentageUsed);
  const isWarning = percentageUsed >= 80 && percentageUsed < 100;
  const isCritical = percentageUsed >= 100;
  const isExceeded = !quota.featureEnabled;

  let statusClass = 'normal';
  let statusIcon = '✓';
  let statusMessage = 'SRO Chat Available';

  if (isExceeded) {
    statusClass = 'exceeded';
    statusIcon = '✕';
    statusMessage = quota.disabledReason || 'SRO Chat Disabled';
  } else if (isCritical) {
    statusClass = 'critical';
    statusIcon = '⚠';
    statusMessage = 'Quota Exceeded';
  } else if (isWarning) {
    statusClass = 'warning';
    statusIcon = '⚡';
    statusMessage = 'Quota Running Low';
  }

  const resetDate = new Date(quota.quotaResetDate);
  const daysUntilReset = Math.ceil((resetDate - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <div className={`quota-status ${statusClass}`}>
      <div className="quota-header">
        <span className="status-icon">{statusIcon}</span>
        <span className="status-message">{statusMessage}</span>
      </div>

      <div className="quota-progress">
        <div className="progress-bar-container">
          <div
            className={`progress-bar ${statusClass}`}
            style={{ width: `${Math.min(percentageUsed, 100)}%` }}
          />
        </div>
        <div className="progress-text">
          <span className="percentage">{percentageUsed.toFixed(1)}%</span>
          <span className="tokens">
            {quota.tokensUsed.toLocaleString()} / {quota.monthlyAllowance.toLocaleString()} tokens
          </span>
        </div>
      </div>

      {showDetails && (
        <div className="quota-details">
          <div className="detail-row">
            <span className="label">Remaining Tokens:</span>
            <span className="value">{quota.tokensRemaining.toLocaleString()}</span>
          </div>
          <div className="detail-row">
            <span className="label">Reset Date:</span>
            <span className="value">
              {resetDate.toLocaleDateString()} ({daysUntilReset} days)
            </span>
          </div>
          {quota.isUnlimited && (
            <div className="detail-row highlight">
              <span className="label">Status:</span>
              <span className="value">Unlimited Plan</span>
            </div>
          )}
        </div>
      )}

      {isExceeded && (
        <div className="quota-alert alert-error">
          <p>Your SRO Chat feature is currently disabled.</p>
          <p className="reason">{quota.disabledReason}</p>
          <a href="/upgrade" className="btn btn-primary">
            Upgrade Plan
          </a>
        </div>
      )}

      {isWarning && !isExceeded && (
        <div className="quota-alert alert-warning">
          <p>You're approaching your monthly token limit.</p>
          <p>Consider upgrading to a higher plan for more tokens.</p>
          <a href="/pricing" className="btn btn-secondary">
            View Plans
          </a>
        </div>
      )}
    </div>
  );
};

export default SROChatQuotaStatus;
